import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogOut, Plus, Pencil, Trash2, FileText, Calendar, Inbox, Loader2, Save, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { LOGO_MAIN } from "@/data/content";

const emptyArticle = { title: "", excerpt: "", content: "", cover_image: "", category: "Sfaturi HoReCa", published: true };
const emptyEvent = { title: "", body: "", image: "", event_date: "" };

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("articles");
  const [articles, setArticles] = useState([]);
  const [events, setEvents] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);

  const [articleEdit, setArticleEdit] = useState(null);
  const [eventEdit, setEventEdit] = useState(null);

  useEffect(() => {
    if (user === null) nav("/admin/login");
    if (user && user.role !== "admin") nav("/admin/login");
  }, [user, nav]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [a, e, l] = await Promise.all([
        api.get("/admin/articles"),
        api.get("/events"),
        api.get("/admin/leads"),
      ]);
      setArticles(a.data);
      setEvents(e.data);
      setLeads(l.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && user.role === "admin") loadAll();
  }, [user]);

  const handleLogout = () => {
    logout();
    nav("/admin/login");
  };

  // --- Articles ---
  const saveArticle = async () => {
    if (!articleEdit) return;
    try {
      if (articleEdit.id) {
        const { id, ...payload } = articleEdit;
        await api.put(`/admin/articles/${id}`, payload);
        toast.success("Articol actualizat.");
      } else {
        await api.post("/admin/articles", articleEdit);
        toast.success("Articol publicat.");
      }
      setArticleEdit(null);
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  const deleteArticle = async (id) => {
    if (!window.confirm("Sigur ștergi articolul?")) return;
    try {
      await api.delete(`/admin/articles/${id}`);
      toast.success("Articol șters.");
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // --- Events ---
  const saveEvent = async () => {
    if (!eventEdit) return;
    try {
      if (eventEdit.id) {
        const { id, ...payload } = eventEdit;
        await api.put(`/admin/events/${id}`, payload);
        toast.success("Eveniment actualizat.");
      } else {
        await api.post("/admin/events", eventEdit);
        toast.success("Eveniment adăugat.");
      }
      setEventEdit(null);
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  const deleteEvent = async (id) => {
    if (!window.confirm("Sigur ștergi evenimentul?")) return;
    try {
      await api.delete(`/admin/events/${id}`);
      toast.success("Eveniment șters.");
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // --- Leads ---
  const deleteLead = async (id) => {
    if (!window.confirm("Sigur ștergi lead-ul?")) return;
    try {
      await api.delete(`/admin/leads/${id}`);
      toast.success("Lead șters.");
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (!user) {
    return <div className="min-h-screen flex items-center justify-center text-text-soft">Se încarcă...</div>;
  }

  return (
    <div data-testid="page-admin-dashboard" className="min-h-screen bg-bg text-text">
      <header className="border-b border-border bg-surface/60 backdrop-blur sticky top-0 z-40">
        <div className="max-w-7xl mx-auto container-px h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_MAIN} alt="DOTAPAN" className="h-9" />
            <div className="hidden sm:block">
              <div className="font-display text-base text-text">DOTAPAN Admin</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-gold">Panel intern</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-xs text-text-soft">{user.email}</span>
            <Button variant="outline" onClick={handleLogout} data-testid="admin-logout" className="rounded-full h-9">
              <LogOut className="h-4 w-4" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto container-px py-10">
        <h1 className="h-display text-3xl md:text-4xl text-text">Bun venit, {user.name || "Admin"}.</h1>
        <p className="text-text-soft mt-2">Gestionează articole, evenimente și solicitări primite.</p>

        <Tabs value={tab} onValueChange={setTab} className="mt-8">
          <TabsList className="bg-surface-2 rounded-full p-1.5 h-auto">
            <TabsTrigger value="articles" className="rounded-full px-5 py-2 text-sm" data-testid="tab-articles">
              <FileText className="h-4 w-4 mr-2" /> Articole ({articles.length})
            </TabsTrigger>
            <TabsTrigger value="events" className="rounded-full px-5 py-2 text-sm" data-testid="tab-events">
              <Calendar className="h-4 w-4 mr-2" /> Evenimente ({events.length})
            </TabsTrigger>
            <TabsTrigger value="leads" className="rounded-full px-5 py-2 text-sm" data-testid="tab-leads">
              <Inbox className="h-4 w-4 mr-2" /> Lead-uri ({leads.length})
            </TabsTrigger>
          </TabsList>

          {/* Articles tab */}
          <TabsContent value="articles" className="mt-8">
            <div className="flex justify-between items-center mb-5">
              <h2 className="font-display text-xl text-text">Articole publicate</h2>
              <Button onClick={() => setArticleEdit({ ...emptyArticle })} className="btn-gold rounded-full h-10" data-testid="add-article">
                <Plus className="h-4 w-4" /> Adaugă articol
              </Button>
            </div>
            {loading ? <Loader2 className="h-5 w-5 animate-spin text-gold" /> : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {articles.map((a) => (
                  <div key={a.id} className="card-soft overflow-hidden" data-testid={`admin-article-${a.id}`}>
                    {a.cover_image && <img src={a.cover_image} alt={a.title} className="w-full h-40 object-cover" />}
                    <div className="p-5">
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <span className="text-[10px] uppercase tracking-[0.2em] text-gold">{a.category}</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${a.published ? "bg-green-500/15 text-green-500" : "bg-gray-500/15 text-gray-400"}`}>
                          {a.published ? "Publicat" : "Draft"}
                        </span>
                      </div>
                      <h3 className="font-display text-lg text-text">{a.title}</h3>
                      <p className="text-xs text-text-soft mt-1">/{a.slug}</p>
                      <div className="flex gap-2 mt-4">
                        <Button size="sm" variant="outline" onClick={() => setArticleEdit(a)} className="rounded-full">
                          <Pencil className="h-3 w-3" /> Editează
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => deleteArticle(a.id)} className="rounded-full text-destructive">
                          <Trash2 className="h-3 w-3" /> Șterge
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                {articles.length === 0 && <p className="text-text-soft">Nu există articole. Adaugă primul.</p>}
              </div>
            )}
          </TabsContent>

          {/* Events tab */}
          <TabsContent value="events" className="mt-8">
            <div className="flex justify-between items-center mb-5">
              <h2 className="font-display text-xl text-text">Feed evenimente</h2>
              <Button onClick={() => setEventEdit({ ...emptyEvent })} className="btn-gold rounded-full h-10" data-testid="add-event">
                <Plus className="h-4 w-4" /> Adaugă eveniment
              </Button>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {events.map((ev) => (
                <div key={ev.id} className="card-soft overflow-hidden" data-testid={`admin-event-${ev.id}`}>
                  {ev.image && <img src={ev.image} alt={ev.title} className="w-full h-40 object-cover" />}
                  <div className="p-5">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-gold">
                      {ev.event_date ? new Date(ev.event_date).toLocaleDateString("ro-RO") : "Eveniment"}
                    </span>
                    <h3 className="font-display text-lg text-text mt-1">{ev.title}</h3>
                    {ev.body && <p className="text-xs text-text-soft mt-2 line-clamp-3">{ev.body}</p>}
                    <div className="flex gap-2 mt-4">
                      <Button size="sm" variant="outline" onClick={() => setEventEdit(ev)} className="rounded-full">
                        <Pencil className="h-3 w-3" /> Editează
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => deleteEvent(ev.id)} className="rounded-full text-destructive">
                        <Trash2 className="h-3 w-3" /> Șterge
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
              {events.length === 0 && <p className="text-text-soft">Niciun eveniment încă.</p>}
            </div>
          </TabsContent>

          {/* Leads tab */}
          <TabsContent value="leads" className="mt-8">
            <h2 className="font-display text-xl text-text mb-5">Solicitări primite</h2>
            <div className="space-y-3">
              {leads.map((l) => (
                <div key={l.id} className="card-soft p-5" data-testid={`admin-lead-${l.id}`}>
                  <div className="flex flex-wrap justify-between gap-3">
                    <div>
                      <div className="font-medium text-text">{l.name} {l.company && <span className="text-text-soft">• {l.company}</span>}</div>
                      <div className="text-xs text-text-soft mt-1 flex flex-wrap gap-3">
                        <a href={`tel:${l.phone}`} className="hover:text-gold">{l.phone}</a>
                        {l.email && <a href={`mailto:${l.email}`} className="hover:text-gold">{l.email}</a>}
                        {l.county && <span>{l.county}</span>}
                        {l.business_type && <span>• {l.business_type}</span>}
                      </div>
                    </div>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-text-soft">
                      {new Date(l.created_at).toLocaleString("ro-RO")}
                    </div>
                  </div>
                  {l.message && <p className="text-sm text-text mt-3 leading-relaxed">{l.message}</p>}
                  <div className="flex gap-2 mt-4">
                    <a href={`https://wa.me/${l.phone.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer">
                      <Button size="sm" variant="outline" className="rounded-full">WhatsApp</Button>
                    </a>
                    <Button size="sm" variant="outline" onClick={() => deleteLead(l.id)} className="rounded-full text-destructive">
                      <Trash2 className="h-3 w-3" /> Șterge
                    </Button>
                  </div>
                </div>
              ))}
              {leads.length === 0 && <p className="text-text-soft">Nicio solicitare încă.</p>}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Article edit dialog */}
      <Dialog open={!!articleEdit} onOpenChange={(o) => !o && setArticleEdit(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="article-dialog">
          <DialogHeader>
            <DialogTitle>{articleEdit?.id ? "Editare articol" : "Articol nou"}</DialogTitle>
          </DialogHeader>
          {articleEdit && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Titlu</Label>
                <Input value={articleEdit.title} onChange={(e) => setArticleEdit({ ...articleEdit, title: e.target.value })} data-testid="article-title" />
              </div>
              <div className="space-y-2">
                <Label>Categorie</Label>
                <Input value={articleEdit.category} onChange={(e) => setArticleEdit({ ...articleEdit, category: e.target.value })} placeholder="Ex: Sfaturi HoReCa" />
              </div>
              <div className="space-y-2">
                <Label>Imagine de copertă (URL)</Label>
                <Input value={articleEdit.cover_image} onChange={(e) => setArticleEdit({ ...articleEdit, cover_image: e.target.value })} placeholder="https://..." />
              </div>
              <div className="space-y-2">
                <Label>Extras (intro scurt)</Label>
                <Textarea rows={2} value={articleEdit.excerpt} onChange={(e) => setArticleEdit({ ...articleEdit, excerpt: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Conținut</Label>
                <Textarea rows={10} value={articleEdit.content} onChange={(e) => setArticleEdit({ ...articleEdit, content: e.target.value })} data-testid="article-content" />
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={articleEdit.published} onCheckedChange={(v) => setArticleEdit({ ...articleEdit, published: v })} />
                <Label>Publicat pe site</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setArticleEdit(null)} className="rounded-full">
              <X className="h-4 w-4" /> Renunță
            </Button>
            <Button onClick={saveArticle} className="btn-gold rounded-full" data-testid="article-save">
              <Save className="h-4 w-4" /> Salvează
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Event edit dialog */}
      <Dialog open={!!eventEdit} onOpenChange={(o) => !o && setEventEdit(null)}>
        <DialogContent className="max-w-xl" data-testid="event-dialog">
          <DialogHeader>
            <DialogTitle>{eventEdit?.id ? "Editare eveniment" : "Eveniment nou"}</DialogTitle>
          </DialogHeader>
          {eventEdit && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Titlu</Label>
                <Input value={eventEdit.title} onChange={(e) => setEventEdit({ ...eventEdit, title: e.target.value })} data-testid="event-title" />
              </div>
              <div className="space-y-2">
                <Label>Dată</Label>
                <Input type="date" value={eventEdit.event_date || ""} onChange={(e) => setEventEdit({ ...eventEdit, event_date: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Imagine (URL)</Label>
                <Input value={eventEdit.image} onChange={(e) => setEventEdit({ ...eventEdit, image: e.target.value })} placeholder="https://..." />
              </div>
              <div className="space-y-2">
                <Label>Descriere</Label>
                <Textarea rows={4} value={eventEdit.body} onChange={(e) => setEventEdit({ ...eventEdit, body: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEventEdit(null)} className="rounded-full">
              <X className="h-4 w-4" /> Renunță
            </Button>
            <Button onClick={saveEvent} className="btn-gold rounded-full" data-testid="event-save">
              <Save className="h-4 w-4" /> Salvează
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
